import React from 'react';
import { Show, useShowContext, useUpdate, useNotify, useRefresh, Button, type RaRecord } from 'react-admin';
import { Box, Typography, Paper, Chip, Stepper, Step, StepLabel, Alert, Grid, LinearProgress, IconButton, Card, CardContent, Avatar } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { PersonAdd, CheckCircle, Schedule, Warning, Close, HourglassEmpty, Assignment } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { ProcessActionsGrouped } from '@/components/workflow/ProcessActions';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import type { OnboardingStep, InfoField } from './constants';
import { DEFAULT_STEPS, STATUS_COLORS, getStepIcon } from './constants';

const OnboardingProcessShowContent = () => {
  const { record } = useShowContext();
  const navigate = useNavigate();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const employeeMap = useEmployeeMap();

  if (!record) return null;

  const steps: OnboardingStep[] = record.steps?.length ? record.steps : DEFAULT_STEPS;
  const completedSteps = steps.filter((s: OnboardingStep) => s.status === 'Completed').length;
  const progress = Math.round((completedSteps / steps.length) * 100);
  const isOverdue = record.targetStartDate && new Date(record.targetStartDate) < new Date() && record.status !== 'Completed';

  const handleAction = (data: Record<string, unknown>) => {
    update('onboarding-processes', { id: record.id, data }, {
      onSuccess: () => { notify('Process updated', { type: 'success' }); refresh(); },
      onError: (e: Error) => { notify(`Error: ${e.message}`, { type: 'error' }); },
    });
  };

  const handleStepToggle = (stepId: string) => {
    const now = new Date().toISOString();
    const updatedSteps = steps.map((s: OnboardingStep) => {
      if (s.stepId !== stepId) return s;
      const isCompleted = s.status === 'Completed';
      const result = { ...s, status: isCompleted ? 'Pending' : 'Completed' as const };
      if (!isCompleted) result.completedAt = now;
      else delete result.completedAt;
      return result;
    });
    const newCompleted = updatedSteps.filter((s: OnboardingStep) => s.status === 'Completed').length;
    const allDone = newCompleted === updatedSteps.length;
    handleAction({
      steps: updatedSteps,
      ...(allDone ? { status: 'Completed', completedAt: now } : {}),
    });
  };

  const makeStepAction = (idx: number, label: string, description: string, isFinal = false) => ({
    label,
    description,
    icon: <CheckCircle />,
    color: (isFinal ? 'success' : 'info') as 'success' | 'info',
    condition: record.status === 'InProgress' && (isFinal ? completedSteps < steps.length : completedSteps === idx),
    onClick: () => {
      const updated = steps.map((s: OnboardingStep, i: number) =>
        i === idx ? { ...s, status: 'Completed', completedAt: new Date().toISOString().split('T')[0] } : s
      );
      if (isFinal) {
        handleAction({ status: 'Completed', completedAt: new Date().toISOString().split('T')[0] });
      } else {
        const allDone = updated.filter((s: OnboardingStep) => s.status === 'Completed').length === updated.length;
        handleAction({ steps: updated, ...(allDone ? { status: 'Completed', completedAt: new Date().toISOString().split('T')[0] } : {}) });
      }
    },
    loading: isLoading,
  });

  const stepActions = [
    makeStepAction(0, 'Complete Personal Details', 'Mark the Personal Details Collection step as completed.'),
    makeStepAction(1, 'Complete Documents', 'Mark the Statutory Document Upload step as completed.'),
    makeStepAction(2, 'Complete Contract Signing', 'Mark the Employment Contract Signing step as completed.'),
    makeStepAction(3, 'Complete POPIA Consent', 'Mark the POPIA Consent step as completed.'),
    makeStepAction(4, 'Complete System Provisioning', 'Mark the System Access Provisioning step as completed. If all steps done, this will complete the onboarding.'),
    makeStepAction(0, 'Complete Onboarding', 'Finalize the onboarding process. All steps must be completed first.', true),
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/onboarding-processes')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <PageDescription entity="Onboarding Process" description="The onboarding workflow for a new employee. Tracks document collection, inductions, and setup tasks." icon={<Assignment />} />
      {isOverdue && <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>Target start date passed — expedite onboarding completion</Alert>}
      {record.status === 'Completed' && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>Employee onboarded successfully</Alert>}

      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)', color: 'white', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}><PersonAdd sx={{ fontSize: 28 }} /></Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{record.processId || 'Onboarding Process'}</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>{getEmployeeName(employeeMap, record.employee?.id)}{record.department ? ` — ${record.department}` : ''}</Typography>
          </Box>
          <Chip label={record.status} size="medium" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, backdropFilter: 'blur(4px)' }} />
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {([
          { label: 'Process ID', value: record.processId, bold: true },
          { label: 'Employee', value: getEmployeeName(employeeMap, record.employee?.id) },
          { label: 'Department', value: record.department },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Initiated', value: record.initiatedAt },
          { label: 'Target Start', value: record.targetStartDate, warning: isOverdue },
          { label: 'Completed', value: record.completedAt || '—' },
        ] as InfoField[]).map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}><Chip label={String(f.value ?? '')} size="small" color={STATUS_COLORS[record.status]} /></Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5, color: f.warning ? 'error.main' : 'inherit' }}>{String(f.value ?? '—')}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2">Onboarding Steps ({completedSteps}/{steps.length})</Typography>
              <Typography variant="caption" color="text.secondary">{progress}% complete</Typography>
            </Box>
            <Stepper activeStep={completedSteps} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((step: OnboardingStep, idx: number) => {
                const isCompleted = step.status === 'Completed';
                const isCurrent = idx === completedSteps;
                return (
                  <Step key={step.stepId} completed={isCompleted} onClick={() => handleStepToggle(step.stepId)} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'grey.300', fontSize: 12, transition: 'all 0.2s' }}>
                          {getStepIcon(step.stepId)}
                        </Avatar>
                      )}
                    >
                      <Typography variant="caption" sx={{ fontWeight: isCurrent ? 700 : 400 }}>{step.stepName}</Typography>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <LinearProgress variant="determinate" value={progress} sx={{ width: '100%', height: 8, borderRadius: 1 }} />
          </Paper>
        </Grid>
      </Grid>

      <ProcessActionsGrouped
        groups={[
          { title: 'Step Completion', actions: stepActions },
          { title: 'Documents', actions: [{ label: 'Print Summary', description: 'Print or save the onboarding summary document for records.', icon: <CheckCircle />, color: 'inherit', variant: 'outlined', condition: record.status === 'Completed', onClick: () => window.print() }] },
        ]}
        isLoading={isLoading}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Step Details</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Click a step card to toggle its status</Typography>
        <Grid container spacing={2}>
          {steps.map((step: OnboardingStep) => {
            const isCompleted = step.status === 'Completed';
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={step.stepId}>
                  <Card
                    variant="outlined"
                    onClick={() => handleStepToggle(step.stepId)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      borderLeft: '4px solid',
                      borderColor: isCompleted ? 'success.main' : step.status === 'InProgress' ? 'primary.main' : 'grey.300',
                      '&:hover': { boxShadow: 1 },
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: isCompleted ? 'success.main' : 'grey.300' }}>
                            {getStepIcon(step.stepId)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.stepName}</Typography>
                        </Box>
                        {isCompleted ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <HourglassEmpty color="disabled" fontSize="small" />
                        )}
                      </Box>
                      <Typography variant="caption" color={step.completedAt ? 'text.secondary' : 'transparent'} sx={{ mt: 0.5, display: 'block', ml: 4, userSelect: 'none' }}>
                        {step.completedAt ? `Completed: ${step.completedAt}` : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button component={Link} to="/employees" variant="outlined" size="small" startIcon={<PersonAdd />}>
          View Employee
        </Button>
        <Button component={Link} to="/contract-alerts" variant="outlined" size="small" startIcon={<Schedule />}>
          Contract Alerts
        </Button>
        <Button component={Link} to="/compliance-calendar" variant="outlined" size="small" startIcon={<CheckCircle />}>
          Compliance Calendar
        </Button>
      </Box>
    </Box>
  );
};

export const OnboardingProcessShow = () => (
  <Show>
    <OnboardingProcessShowContent />
  </Show>
);
